// Core gtag event dispatcher
export function trackEvent(eventName: string, eventParams: Record<string, any> = {}) {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', eventName, eventParams);
  }
}

// ─── Auth Events ────────────────────────────────────────────────────────────

/** Fired when the user switches between Sign In and Create Account tabs */
export function trackAuthModeSwitch(mode: 'login' | 'signup') {
  trackEvent('auth_mode_switch', { mode });
}

/** Fired when the login form is submitted */
export function trackLoginAttempt(method: string = 'email') {
  trackEvent('login', { method });
}

/** Fired after a successful login */
export function trackLoginSuccess(role: string) {
  trackEvent('login_success', { user_role: role });
}

/** Fired when login fails */
export function trackLoginFailure(reason: string) {
  trackEvent('login_failure', { reason });
}

/** Fired when the sign-up form is submitted */
export function trackSignUpAttempt(accountType: string) {
  trackEvent('sign_up_attempt', { account_type: accountType });
}

/** Fired after a successful sign-up */
export function trackSignUpSuccess(accountType: string) {
  trackEvent('sign_up', { account_type: accountType });
}

/** Fired when sign-up fails */
export function trackSignUpFailure(reason: string) {
  trackEvent('sign_up_failure', { reason });
}

/** Fired when the forgot-password flow is initiated */
export function trackForgotPasswordRequest() {
  trackEvent('forgot_password_request');
}

// ─── Checkout Events ─────────────────────────────────────────────────────────

/** Fired when the user selects a subscription plan */
export function trackPlanSelected(planId: string, planName: string, price: number) {
  trackEvent('select_item', {
    item_list_id: 'subscription_plans',
    items: [{ item_id: planId, item_name: planName, price }],
  });
}

/** Fired when a promo code is applied */
export function trackPromoApplied(code: string) {
  trackEvent('promo_code_applied', { promo_code: code });
}

/** Fired when a promo code is removed */
export function trackPromoRemoved() {
  trackEvent('promo_code_removed');
}

/** Fired when the user clicks "Continue to Payment" */
export function trackCheckoutBegin(planId: string, planName: string, price: number, promoApplied: boolean) {
  trackEvent('begin_checkout', {
    currency: 'USD',
    value: promoApplied ? 0 : price,
    items: [{ item_id: planId, item_name: planName, price }],
    coupon: promoApplied ? 'SAFEPLAY1FREE' : undefined,
  });
}

/** Fired when payment is submitted */
export function trackPaymentAttempt(planId: string, isFreeTrialSetup: boolean) {
  trackEvent('payment_attempt', { plan_id: planId, is_free_trial: isFreeTrialSetup });
}

/** Fired after a successful subscription activation */
export function trackPurchase(planId: string, planName: string, price: number, promoApplied: boolean) {
  trackEvent('purchase', {
    transaction_id: `txn_${Date.now()}`,
    currency: 'USD',
    value: promoApplied ? 0 : price,
    items: [{ item_id: planId, item_name: planName, price }],
  });
}

/** Fired when payment fails */
export function trackPaymentFailure(reason: string) {
  trackEvent('payment_failure', { reason });
}

// ─── Dashboard Events ────────────────────────────────────────────────────────

/** Fired when the user types in the platform search box */
export function trackDashboardSearch(query: string) {
  trackEvent('dashboard_search', { search_term: query });
}

/** Fired when the user changes the status filter */
export function trackDashboardStatusFilter(filter: string) {
  trackEvent('dashboard_filter', { filter_type: 'status', filter_value: filter });
}

/** Fired when the user changes the category filter */
export function trackDashboardCategoryFilter(category: string) {
  trackEvent('dashboard_filter', { filter_type: 'category', filter_value: category });
}
